import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { getAccessSummary } from "@/features/access/accessApi";
import type { AccessSummary } from "@/features/access/accessTypes";

type AccessSummaryState = {
  access: AccessSummary | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useAccessSummary(): AccessSummaryState {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [access, setAccess] = useState<AccessSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadAccess() {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      setAccess(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await getAccessSummary(token);
      setAccess(response.access);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load access");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void loadAccess();
  }, [isLoaded, isSignedIn]);

  return {
    access,
    isLoading: !isLoaded || isLoading,
    error,
    refresh: loadAccess,
  };
}
